// English: every key the translated screens use, worded exactly as the screens were before translation. Other
// languages fall back to this file for any key they don't have, so it must stay complete (tests check it).
export const EN: Record<string, string> = {
  // ---- Sign-in screens -------------------------------------------------------------------------------------------
  "lang.label": "Language",
  "auth.brandSub": "Product lifecycle management",
  "auth.wait": "Please wait…",
  "auth.loading": "Loading…",
  "auth.signIn": "Sign in",
  "auth.signInSub": "Use the email address your organisation registered for you.",
  "auth.passwordSaved": "Your password is saved. Sign in to continue.",
  "auth.sessionEnded": "Your session ended. Please sign in again.",
  "auth.email": "Email address",
  "auth.password": "Password",
  "auth.forgotLink": "Forgot your password?",
  "auth.enterEmailPassword": "Enter your email address and password.",
  "auth.twoFactorCheck": "Two-factor check",
  "auth.recoverySub": "Enter one of the recovery codes you saved when you set up two-factor login. Each code works once.",
  "auth.codeSub": "Open your authenticator app and enter the 6-digit code shown for Falcon PLM.",
  "auth.recoveryCode": "Recovery code",
  "auth.sixDigit": "6-digit code",
  "auth.continue": "Continue",
  "auth.useApp": "Use my authenticator app instead",
  "auth.useRecovery": "Lost your phone? Use a recovery code",
  "auth.cancelSignOut": "Cancel and sign out",
  "auth.recoveryAccepted": "Recovery code accepted. You have {n} left; create new ones in My Account.",
  "auth.resetTitle": "Reset your password",
  "auth.resetSub": "Enter your email address and we'll send you a link to choose a new password.",
  "auth.sendReset": "Send reset link",
  "auth.backToSignIn": "Back to sign in",
  "auth.resetSent": "If that email has a Falcon account, a reset link is on its way. It expires in 1 hour.",
  "auth.linkUnusable": "This link can't be used",
  "auth.requestNewLink": "Request a new link",
  "auth.welcome": "Welcome",
  "auth.welcomeName": "Welcome, {name}",
  "auth.chooseNew": "Choose a new password",
  "auth.inviteSub": "Choose a password to activate your Falcon PLM account.",
  "auth.resetPasswordSub": "Choose a new password for your Falcon PLM account.",
  "auth.otherUser": "This browser is signed in as {other}. Continuing signs that account out here, so {email} starts on its own.",
  "auth.newPassword": "New password",
  "auth.passwordHint": "At least 10 characters. A short sentence is easy to remember and hard to guess.",
  "auth.typeAgain": "Type it again",
  "auth.activate": "Activate my account",
  "auth.saveNewPassword": "Save new password",
  "auth.noMatch": "The two passwords don't match.",
  "auth.2fa.title": "Set up two-factor login",
  "auth.2fa.required": "Two-factor login is required for you (as an Admin, or because your organisation requires it for everyone). It stops anyone who learns your password from getting in.",
  "auth.2fa.step1": "{b}Install an authenticator app{eb} on your phone, for example Google Authenticator or Microsoft Authenticator (free).",
  "auth.2fa.step2": "{b}Scan this code{eb} with the app (tap + then \"Scan a QR code\").",
  "auth.2fa.qrAlt": "QR code for your authenticator app",
  "auth.2fa.cantScan": "Can't scan? Enter this key in the app instead:",
  "auth.2fa.step3": "{b}Enter the 6-digit code{eb} the app now shows for Falcon PLM.",
  "auth.2fa.turnOn": "Turn on two-factor login",
  "auth.2fa.on": "Two-factor login is on.",
  "auth.2fa.saveCodes": "{b}Save these recovery codes now.{eb} If you lose your phone, each code lets you sign in once. Keep them somewhere safe, such as a password manager or a printed copy locked away. They won't be shown again.",
  "auth.2fa.copy": "Copy codes",
  "auth.2fa.download": "Download as text file",
  "auth.2fa.saved": "I've saved them · Continue",
  "auth.err.BAD_LOGIN": "Email or password is incorrect.",
  "auth.err.LOCKED": "Too many wrong attempts. Try again later, or reset your password.",
  "auth.err.BAD_CODE": "That code is not correct. Check the time on your phone and try the newest code.",
  "auth.err.LINK_EXPIRED": "This link has expired or has already been used. Ask for a new one.",
  "auth.err.NOT_SIGNED_IN": "Your sign-in has expired. Please sign in again.",
  "auth.err.MFA_REQUIRED": "Enter the 6-digit code from your authenticator app.",
  // ---- The QC inspector's Home (Ready for QC, My inspections) ------------------------------------------------------
  "qch.ready": "Ready for QC",
  "qch.readySub": "Purchase orders whose bulk production is ready for inspection.",
  "qch.draftSaved": "Draft saved",
  "qch.inProgress": "In progress",
  "qch.colour": "Colour",
  "qch.productType": "Product type",
  "qch.quantity": "Quantity",
  "qch.inspection": "Inspection",
  "qch.inspecting": "{name} is inspecting this line.",
  "qch.another": "Another inspector",
  "qch.continue": "Continue inspection",
  "qch.start": "Start inspection",
  "qch.actionNeeded": "Action needed",
  "qch.replies": "{n} replies",
  "qch.openAnswer": "Open {report} and answer",
  "qch.actionFromYou": "Action needed from you",
  "qch.nothingReady": "Nothing is ready for QC right now. Lines appear here once their bulk production is under way.",
  "qch.newOnReports": "New on your reports",
  "qch.mine": "My inspections",
  "qch.stepByStep": "Step-by-step inspections",
  "qch.mineSub": "Drafts and submitted reports.",
  "qch.noInspections": "No inspections yet. Start one from Ready for QC.",
  "qch.round1": "1st QC",
  "qch.round2": "2nd QC",
  "qch.roundN": "QC{n}",
  // ---- Product words -------------------------------------------------------------------------------------------------
  "pw.garment": "Garment",
  "pw.footwear": "Footwear",
  "pw.product": "Product",
  "pw.general": "General",
  "pw.measuredGarments": "Garments measured",
  "pw.measuredPairs": "Pairs measured",
  "pw.measuredPieces": "Pieces measured",
  "pw.trims": "Trims and artwork",
  "pw.components": "Components",
  "pw.pcs": "pcs",
  "pw.pairs": "pairs",
  "pw.piecesWord": "pieces",
  "pw.pairsWord": "pairs",
  // ---- The five steps, sections, severity ------------------------------------------------------------------------------
  "step.inspection": "Inspection",
  "step.checks": "Photos and checks",
  "step.workmanship": "Workmanship",
  "step.measurements": "Measurements",
  "step.result": "Result",
  "sec.packing": "Packing",
  "sec.labels": "Labels and tags",
  "sec.other": "Other checks",
  "sec.video": "Video",
  "sev.none": "No defect",
  "sev.minor": "Minor",
  "sev.major": "Major",
  "sev.critical": "Critical",
  "lvl.I": "General level I",
  "lvl.II": "General level II",
  "lvl.III": "General level III",
  "lvl.S-1": "Special S-1",
  "lvl.S-2": "Special S-2",
  "lvl.S-3": "Special S-3",
  "lvl.S-4": "Special S-4",
  "lvl.100%": "100% (every piece)",
  // ---- Red boxes -----------------------------------------------------------------------------------------------------
  "msg.photo": "Add at least one photo",
  "msg.answer": "Answer yes or no",
  "msg.reason": "Say what is wrong",
  "msg.required": "This is required",
  "msg.checkedOver": "Can't be more than the finished quantity",
  "msg.point": "Complete the point, size and measured size",
  "msg.addedPhoto": "Add a photo or remove this check",
  "msg.title": "Enter a title",
  // ---- Built-in checks (Falcon's default wording; a renamed check shows its own name) ----------------------------------
  "chk:Garment front": "Garment front",
  "chk:Garment back": "Garment back",
  "chk:Inside and seams": "Inside and seams",
  "chk:Carton and carton marks": "Carton and carton marks",
  "chk:Carton label": "Carton label",
  "chk:Polybag": "Polybag",
  "chk:Polybag sticker": "Polybag sticker",
  "chk:Folding": "Folding",
  "chk:Swing tag": "Swing tag",
  "chk:Wash care label": "Wash care label",
  "chk:Brand label": "Brand label",
  "chk:Size label": "Size label",
  "chk:Pair side view": "Pair side view",
  "chk:Top view": "Top view",
  "chk:Sole": "Sole",
  "chk:Inside and insole": "Inside and insole",
  "chk:Toe cap and heel": "Toe cap and heel",
  "chk:Shoe box": "Shoe box",
  "chk:Tissue and stuffing": "Tissue and stuffing",
  "chk:Box label": "Box label",
  "chk:Front": "Front",
  "chk:Back": "Back",
  "chk:Inside or detail": "Inside or detail",
  "chk:Inner packing": "Inner packing",
  "chk:Product label": "Product label",
  // ---- The inspection ------------------------------------------------------------------------------------------------
  "qcw.title": "QC inspection",
  "qcw.steps": "Steps",
  "qcw.stepOf": "step {n} of {total}",
  "qcw.moreActions": "More actions",
  "qcw.close": "Close",
  "qcw.removeDraft": "Remove draft",
  "qcw.back": "Back",
  "qcw.next": "Next",
  "qcw.submit": "Submit report",
  "qcw.nOf": "{n} of {total}",
  "qcw.alertOne": "1 box needs completing before you continue",
  "qcw.alertMany": "{n} boxes need completing before you continue",
  "qcw.everyPiece": "every piece (the lot is smaller than the sample)",
  "qcw.everyPair": "every pair (the lot is smaller than the sample)",
  "qcw.codeLetter": "code letter {letter}",
  "qcw.enterFinished": "Enter the finished quantity",
  "qcw.belowNote": "Below the sample required by the standard ({n} of {total})",
  "qcw.overOrder": "More than the order quantity ({n} {unit}). Check the number.",
  "qcw.sizeTotal": "Total {n} of {total} {unit}",
  "qcw.sizeNoteChecked": "The sizes checked add up to {n}, not the quantity checked of {total}. You can still continue.",
  "qcw.sizeNoteSample": "The sizes checked add up to {n}, not the sample size of {total}. You can still continue.",
  "qcw.tagWarn": "More photos are tagged {list} than the number entered. Check the counts.",
  "qcw.shouldBe": "Should be {spec}, tolerance {tol}",
  "qcw.tolNotGiven": "not given",
  "qcw.choosePoint": "Choose the point and size",
  "qcw.diffWithin": "Difference {diff} · within tolerance",
  "qcw.diffOutside": "Difference {diff} · {beyond} outside tolerance",
  "qcw.standard": "ISO 2859-1 (ANSI/ASQ Z1.4), normal inspection, single sampling · {level} · AQL Critical {c}, Major {m}, Minor {n}",
  "qcw.po": "PO",
  "qcw.style": "Style",
  "qcw.orderQty": "Order quantity",
  "qcw.techpack": "Tech pack",
  "qcw.newerRev": "Newer revision exists (Rev {n})",
  "qcw.noTechpack": "No tech pack issued with this PO.",
  "qcw.noTechpackShort": "No tech pack issued with this PO",
  "qcw.sealPhotos": "{name} photos",
  "qcw.noPhotos": "No photos.",
  "qcw.finishedQty": "Finished quantity available for inspection ({unit})",
  "qcw.packedReady": "Packed and ready {unitWord} of this PO only",
  "qcw.checkedQty": "Quantity checked ({unit})",
  "qcw.piecesChecked": "Pieces you actually checked",
  "qcw.pairsChecked": "Pairs you actually checked",
  "qcw.sampleRequired": "Sample required by the standard",
  "qcw.viewTable": "View table",
  "qcw.reasonOptional": "Reason (optional)",
  "qcw.reason": "Reason",
  "qcw.sizesChecked": "Sizes checked (optional)",
  "qcw.piecesInSize": "Pieces checked in size {size}",
  "qcw.noSizes": "No sizes on this PO line.",
  "qcw.refCaption": "From tech pack Rev {rev}, issued with {po}",
  "qcw.noRef": "No reference in tech pack",
  "qcw.refFor": "Reference for {name}",
  "qcw.severity": "Severity",
  "qcw.photo": "Photo",
  "qcw.photoWaiting": "Photo waiting to upload",
  "qcw.noteOptional": "Note (optional)",
  "qcw.photoNote": "Photo note",
  "qcw.removePhoto": "Remove photo",
  "qcw.waitingUpload": "Waiting to upload…",
  "qcw.optional": "(optional)",
  "qcw.showRef": "Show the reference from the tech pack",
  "qcw.refTitle": "Reference from the tech pack",
  "qcw.matches": "Matches the tech pack?",
  "qcw.yes": "Yes",
  "qcw.no": "No",
  "qcw.whatWrong": "What is wrong?",
  "qcw.defectsFound": "Defects found in the {n} {unit} checked",
  "qcw.noSpec": "No size spec in tech pack",
  "qcw.pomUnit": "Point of measure ({unit})",
  "qcw.tolerance": "Tolerance",
  "qcw.size": "Size",
  "qcw.point": "Measurement point",
  "qcw.requiredSize": "Required size",
  "qcw.toleranceOptional": "Tolerance (optional)",
  "qcw.measuredSize": "Measured size",
  "qcw.pieceNo": "Piece no.",
  "qcw.pieceNoOptional": "Piece number (optional)",
  "qcw.remove": "Remove",
  "qcw.allWithin": "All within tolerance?",
  "qcw.sizeSpec": "Size spec",
  "qcw.pointsOut": "Points out of tolerance",
  "qcw.noSpecType": "No size spec in tech pack: type the point, the required size and the measured size.",
  "qcw.addPoint": "Add another point",
  "qcw.pointNotInSpec": "Point not in the size spec",
  "qcw.aqlHead": "Checked {n} {unit} · standard requires {total} {unit} · AQL {c} / {m} / {x}",
  "qcw.pass": "Pass",
  "qcw.fail": "Fail",
  "qcw.incomplete": "Incomplete",
  "qcw.sampleBelow": "Sample below standard: checked {n} {unit}, standard requires {total} {unit}",
  "qcw.sampleMet": "Sample meets the standard",
  "qcw.found": "Found",
  "qcw.allowed": "Allowed",
  "qcw.ok": "OK",
  "qcw.over": "Over",
  "qcw.includesMeasurements": "includes {n} from measurements",
  "qcw.takePhoto": "Take photo",
  "qcw.chooseGallery": "Choose from gallery",
  "qcw.uploadComputer": "Upload from computer",
  "qcw.addPhotos": "Add photos",
  "qcw.srcCamera": "Camera",
  "qcw.srcUpload": "Uploaded from computer",
  "qcw.srcGallery": "Gallery",
  "qcw.srcGalleryTaken": "Gallery · taken {date}",
  "qcw.takenBefore": "Taken before this inspection was started",
  "qcw.beforeStarted": "before the inspection started",
  "qcw.checksHelp": "Open a section, take the photos and answer each check. Only one section is open at a time.",
  "qcw.trimsFrom": "From tech pack Rev {rev} · {n} trims",
  "qcw.trimsFromOne": "From tech pack Rev {rev} · {n} trim",
  "qcw.otherIntro": "For anything that does not belong in the sections above.",
  "qcw.nothingToCheck": "Nothing to check",
  "qcw.addCheck": "Add another check",
  "qcw.addCheckTo": "Add another check to {title}",
  "qcw.checkTitle": "Title of the check",
  "qcw.titlePh": "Title",
  "qcw.addedByInspector": "Added by inspector",
  "qcw.removeCheck": "Remove this check",
  "qcw.isOk": "Is it OK?",
  "qcw.notesOptional": "Notes (optional)",
  "qcw.recordVideo": "Record video",
  "qcw.uploadVideo": "Upload a video",
  "qcw.overallVideo": "Overall video",
  "qcw.videoPreparing": "being prepared (720p)…",
  "qcw.replacesVideo": "{label} (replaces the video)",
  "qcw.videoMax": "About 2 minutes at most.",
  "qcw.addedCheck": "Added check",
  "qcw.piece": "piece",
  "qcw.shouldMeasured": "should be {spec}, measured {measured}",
  "qcw.noPointsOut": "No points outside tolerance.",
  "qcw.answeredNo": "Answered No",
  "qcw.commentsOptional": "Comments (optional)",
  "qcw.waitingConnection": "Waiting for connection",
  "qcw.saving": "Saving…",
  "qcw.saved": "Saved",
  "qcw.notPicture": "That file isn't a picture.",
  "qcw.videoTooLong": "The video is longer than about 2 minutes. Record a shorter one.",
  "qcw.uploading": "Uploading {n}%",
  "qcw.videoFailed": "The video could not be uploaded.",
  "qcw.videoUploaded": "Video uploaded. It is being prepared (720p).",
  "qcw.removeVideoQ": "Remove the video?",
  "qcw.titleLabel": "Title",
  "qcw.addCheckNote": "The check is added to {section}, marked \"Added by inspector\". It needs at least one photo and Yes or No.",
  "qcw.cancel": "Cancel",
  "qcw.addCheckBtn": "Add check",
  "qcw.enterTitle": "Enter a title.",
  "qcw.removeCheckQOne": "Remove the check \"{title}\" and its {n} photo?",
  "qcw.removeCheckQ": "Remove the check \"{title}\" and its {n} photos?",
  "qcw.removePhotoQ": "Remove this photo?",
  "qcw.removeDraftQ": "Remove this draft? Its photos and answers are deleted.",
  "qcw.draftRemoved": "Draft removed.",
  "qcw.stepIncomplete": "\"{step}\" isn't complete yet.",
  "qcw.photosWaiting": "Some photos are still waiting to upload. Wait until it says Saved.",
  "qcw.submitQ": "Submit the report? After that it is locked: you can only add more details.",
  "qcw.submitted": "Report submitted.",
  "qcw.writeEnglish": "(write in English)",
  "qcw.writeEnglishShort": "write in English",
  "qcw.err.QC_INCOMPLETE": "The report isn't complete yet. The first step with something missing is open.",
  // ---- Defect Classification Guide ------------------------------------------------------------------------------------
  "guide.button": "Defect guide",
  "guide.title": "Defect Classification Guide",
  "guide.reminder": "Inspector reminder only — this guide is not included in the saved inspection report or PDF.",
  "guide.close": "Close",
  "guide.nounGarment": "garment",
  "guide.nounFootwear": "footwear",
  "guide.nounProduct": "product",
  "guide.NounGarment": "Garment",
  "guide.NounFootwear": "Footwear",
  "guide.NounProduct": "Product",
  "guide.criticalWhat": "Safety / compliance risk",
  "guide.criticalNote": "Normally one Critical defect means the inspection should fail.",
  "guide.c1": "Incorrect FR / non-FR material",
  "guide.c2": "Dangerous sharp object left in {item}",
  "guide.c3": "Missing required protective component",
  "guide.c4": "Product incorrectly labelled as meeting a safety standard",
  "guide.c5": "Anything creating an immediate safety or compliance problem",
  "guide.majorWhat": "Function / customer rejection",
  "guide.majorNote": "A serious defect affecting use, performance or the expected specification.",
  "guide.m1": "Broken zip",
  "guide.m2": "Major seam failure",
  "guide.m3": "Reflective tape missing",
  "guide.m4": "Wrong label or wrong colour",
  "guide.m5": "Significant measurement outside tolerance",
  "guide.m6": "Logo / artwork badly positioned",
  "guide.m7": "{Item} not usable as intended",
  "guide.minorWhat": "Cosmetic / small workmanship issue",
  "guide.minorNote": "A small issue that doesn't materially affect safety or normal function.",
  "guide.n1": "Loose thread",
  "guide.n2": "Slight stitch inconsistency",
  "guide.n3": "Small cosmetic mark",
  "guide.n4": "Minor label positioning issue",
  "guide.priority": "Where a customer specification, the approved Tech Pack, a PPE requirement or an applicable standard defines the classification, that takes priority.",
  // ---- The QC standard's table ------------------------------------------------------------------------------------------
  "std.title": "QC standard: sample and defects allowed",
  "std.fewer": "If fewer pieces are checked than required, the defects allowed follow the quantity actually checked.",
  "std.all": "100%: every finished piece is checked and no defects are allowed.",
  "std.finished": "Finished quantity (pcs)",
  "std.sample": "Sample required",
  "std.critical": "Critical allowed",
  "std.major": "Major allowed",
  "std.minor": "Minor allowed",
  "std.andMore": " and more",
  "std.orAll": " or all if fewer",
  // ---- Legal documents: the agreement screen and the footer links (the documents themselves stay English) -------------
  "legal.title": "Before you continue",
  "legal.intro": "Please read the documents below and agree to them to use Falcon PLM in {org}.",
  "legal.updated": "The legal documents have been updated (version {version}). Please read them and agree again to continue.",
  "legal.terms": "Terms of service",
  "legal.privacy": "Privacy policy",
  "legal.dpa": "Data processing agreement",
  "legal.subprocessors": "Sub-processors",
  "legal.agreeTerms": "I agree to the {terms} and have read the {privacy}",
  "legal.agreeDpa": "I accept the {dpa} on behalf of {org}",
  "legal.dpaNote": "As the main admin, you also accept the Data processing agreement for your organisation.",
  "legal.englishDocs": "The documents open in a new tab. They are in English.",
  "legal.continue": "Agree and continue",
  "legal.tickTerms": "Tick the box to agree before continuing.",
  "legal.tickDpa": "Tick the box to accept the Data processing agreement before continuing.",
  "legal.version": "Version {version}",
  "legal.englishNote": "This document is in English. If anything is unclear, please ask your organisation's admin.",
  "legal.footTerms": "Terms",
  "legal.footPrivacy": "Privacy",
  "legal.footDpa": "Data processing",
  "legal.footSub": "Sub-processors",
};
